/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * ProgressBarWidget
 *
 * @file ProgressBarWidget.jsx
 * @author Mohamed Yasar arabath M
 */

import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import mixins from '../../assets/styles/mixins.scss';
import { getStatusStyles } from '../../common/statusColorUtils';

const ProgressBarContainer = styled('div')({
  '&&': {
    display: 'flex',
    width: '100%',
    padding: '2px',
    borderRadius: '8px',
    height: 10
  }
});

const Segment = styled('div')({
  '&&': {
    borderRadius: 8,
    transition: 'width 0.4s cubic-bezier(.47,1.64,.41,.8)'
  }
});

// Segments: green, blue, yellow widths in percent (sum <= 100)
const ProgressBarWidget = (props) => {
  const { segments, height, gap, backgroundColor, onHover } = props;
  // Segment breakdown: first 33%, next 33%, last

  return (
    <ProgressBarContainer
      style={{ gap: (gap ? 2 : 0), background: (backgroundColor || mixins.quaternary) }}
      onMouseEnter={() => onHover(props)}
    >
      {segments.map(
        (segment, idx) => (segment.value ? (
          <Segment
            key={segment.label || idx}
            style={{
              background: segment.color || getStatusStyles(segment?.id)?.dotColor,
              width: `${segment.value}%`,
              height
            }}
          />
        ) : null)
      )}
    </ProgressBarContainer>
  );
};

export default ProgressBarWidget;

ProgressBarWidget.propTypes = {
  segments: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired
    })
  ).isRequired,
  height: PropTypes.number,
  gap: PropTypes.bool,
  backgroundColor: PropTypes.string,
  onHover: PropTypes.func
};

ProgressBarWidget.defaultProps = {
  height: 6,
  gap: true,
  backgroundColor: mixins.quaternary,
  onHover: () => {}
};
